<template>
    <div>
        <FamilyStatementConflictComments></FamilyStatementConflictComments>
        <AlertDismiss class="mx-2" ref="alertDismiss"></AlertDismiss>

        <b-table :busy="isBusy"
            :fields="fields"
            :items="filteredItems"
            sticky-header
            responsive
            no-border-collapse
            selectable
            select-mode="single"
            @row-selected="onRowSelected"
            show-empty
        >
            <template #empty>
                <b-card v-if="items.length !== 0" class="text-center my-2 fs-5">
                    No hay conflictos que coincidan con los criterios de búsqueda
                </b-card>
                <b-card v-else class="text-center my-2 fs-5">
                    Aún no se ha procesado ningún conflicto de interés
                </b-card>
            </template>

            <template #table-busy>
                <div class="text-center text-danger my-2">
                    <b-spinner class="align-middle"></b-spinner>
                    <strong>Cargando datos de tabla...</strong>
                </div>
            </template>

            <!--boton de detalles -->
            <template #cell(details)="row">
                <b-button
                    class="mr-2"
                    size="sm"
                    variant="outline-secondary"
                    @click.stop="row.toggleDetails()"
                    v-b-tooltip.hover="'Detalles'"
                >
                     <b-icon font-scale="1" :icon="row.detailsShowing ? 'eye-slash-fill' : 'eye-fill'"></b-icon>
                </b-button>
            </template>

            <template #cell(selected)="{ rowSelected }">
                <template v-if="rowSelected">
                    <div class="d-flex align-items-center">
                        <b-button @click="openModalComments"
                                  size="sm"
                                  :variant="selected.comment ? 'outline-success' : 'outline-secondary'"
                                  class="ms-2"
                                  v-b-tooltip.hover="selected.comment ? 'Ver Comentario' : 'Añadir Comentario'">
                            <b-icon :icon="selected.comment ? 'chat-left-text' : 'chat-left-text-fill'" font-scale="1"></b-icon>
                        </b-button>
                    </div>
                </template>
                <template v-else>
                    <b-icon icon="three-dots" font-scale="1" class="icone-not-select"></b-icon>
                </template>
            </template>

            <!--cuerpo de detalle-->
            <template #row-details="row">
                <div class="card">
                    <div class="card-body m-0 p-3">
                        <h6>Declarantes en conflicto</h6>
                        <b-table-lite
                            :items="row.item.declarantQueries"
                            :fields="declarantQueriesFields"
                            small
                            responsive
                            striped
                            hover
                        ></b-table-lite>
                    </div>
                </div>
            </template>
        </b-table>
    </div>
</template>
<script>
import AlertDismiss from '@/components/shared/Alert-dismiss.vue'
import FamilyStatementConflictComments from './Family-statement-conflict-comments.vue';
import { formatDateTime, formatRut } from '@/plugins/complianceScripts';
import { mapActions } from 'vuex';
import { EventNames, InitPageSize } from '@/plugins/enums/index.js'

export default {
    name: 'FamilyStatementConflictTable',

    components:{
        AlertDismiss,
        FamilyStatementConflictComments
    },

    data() {
        return {
            isBusy: false,
            items: [],
            searchQuery: '',
            selected: null,

            fields: [
                { key: 'name', label: 'Nombre Sociedad' },
                { key: 'rut', label: 'RUT', formatter: formatRut },
                { key: 'creationDate', label: 'Fecha de Creación', formatter: formatDateTime},
                { key: 'lastModifiedDate', label: 'Fecha de Modificación', formatter: formatDateTime},
                { key: 'details', label: 'Detalles', thClass: 'text-center col-1' },
                { key: 'selected', label: 'Opciones', thClass: 'text-center col-1' }
            ],
            declarantQueriesFields: [
                { key: 'name', label: 'Nombre' },
                { key: 'mail', label: 'Correo' }
            ]
        }
    },

    async mounted(){
        this.$eventBus.$on(EventNames.FilterFamilyConflictNotificationsAfterSearch, (query) => {
            this.isBusy = true;
            this.searchQuery = query
            this.isBusy = false;
        });

        this.$eventBus.$on(EventNames.PaginationFilterFamilyConflictNotifications, async (query) => {
            this.isBusy = true;
            const page = query.page - 1; //se resta 1 por que el indice comienza en 0
            const pageSize = query.pageSize;
            await this.getAllConflicts(page, pageSize);
            this.isBusy = false;
        });

        this.$eventBus.$on(EventNames.UpdatedCommentFamilyStatementConflict, async (data) => {
            const itemIndex = this.items.findIndex(item => item.id === data.conflictId);
            if (itemIndex !== -1) {
                this.$set(this.items[itemIndex], 'comment', data.newComment);
                 // Also update the selected item if it's the one being edited
                if (this.selected && this.selected.id === data.conflictId) {
                    this.$set(this.selected, 'comment', data.newComment);
                }
            }
        });
    },

    computed: {
        filteredItems() {
            let filtered = this.items;
            // Filtrar por búsqueda
            if (this.searchQuery) {
                filtered = this.filterForSearch(filtered, this.searchQuery)
            }
            return filtered;
        }
    },

    methods:{
        ...mapActions(["getAllConflictsOfInterest"]),

        onRowSelected(items) {
            this.selected = items[0] ?? null;
        },

        openModalComments() {
            if (this.selected) {
                this.$eventBus.$emit(EventNames.OpenModalFamilyStatementConflictComments, {
                    conflictId: this.selected.id,
                    currentComment: this.selected.comment || ''
                });
            }
        },

        filterForSearch(itemsToFilter, searchQuery) {
            const normalizedQuery = searchQuery.toLowerCase().trim();
            if (!normalizedQuery) {
                return itemsToFilter;
            }

            return itemsToFilter.filter(item => {
                const name = (item.name || '').toString().toLowerCase();
                const rut = (item.rut || '').toString().toLowerCase();
                const position = (item.position || '').toString().toLowerCase();
                const period = (item.period ? new Date(item.period).toLocaleDateString() : '').toLowerCase();
                const creationDate = (item.creationDate ? new Date(item.creationDate).toLocaleDateString() : '').toLowerCase();
                const modificationDate = (item.modificationDate ? new Date(item.modificationDate).toLocaleDateString() : '').toLowerCase();

                return name.includes(normalizedQuery) ||
                       rut.includes(normalizedQuery) ||
                       position.includes(normalizedQuery) ||
                       period.includes(normalizedQuery) ||
                       creationDate.includes(normalizedQuery) ||
                       modificationDate.includes(normalizedQuery);
            });
        },

        async getAllConflicts(page, pageSize){
            this.isBusy = true;
            this.items = []
            const result = await this.getAllConflictsOfInterest({
                page: page,
                pageSize: pageSize || InitPageSize.ConflictOfInterestPageSize
            });

            if (result.isError) {
                this.$refs.alertDismiss.showAlert(result.message);
                this.items = [];
                this.isBusy = false;
                return;
            }

            this.items = result.data.map(apiItem => ({
                id: apiItem.id,
                name: apiItem.name,
                rut: apiItem.rut,
                position: apiItem.position,
                period: apiItem.period,
                comment: apiItem.comment || '', // Keep comment in data for modal
                isNotified: apiItem.isNotified,
                creationDate: apiItem.creationDate,
                modificationDate: apiItem.modificationDate,
                declarantQueries: apiItem.declarantQueries || []
            }));
            this.isBusy = false;
        },
    }
}
</script>